import CheckIcon from "@shared/svg/check.svg?react";
import ChevronDownIcon from "@shared/svg/chevron-down.svg?react";
import LogoutIcon from "@shared/svg/logout.svg?react";
import MakulaLogoIcon from "@shared/svg/makula-logo-dark.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import { Plus } from "phosphor-react";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { BookDemoModal, type BookDemoFormData } from "./BookDemoModal";
import { MakulaIcon } from "../MakulaIcon";

export interface WorkspaceItem {
  id: string;
  name: string;
  type?: "OEM" | "PORTAL";
  oem_id?: string;
  icon?: string;
  plan?: string;
  members?: number;
}

export interface WorkspaceSwitcherProps {
  currentWorkspace: WorkspaceItem;
  workspaces: WorkspaceItem[];
  userEmail?: string;
  isCollapsed?: boolean;
  disabled?: boolean;
  onWorkspaceChange?: (workspace: WorkspaceItem) => void;
  onAddWorkspace?: (data: BookDemoFormData) => Promise<boolean>;
  onLogout?: () => void;
}

/**
 * WorkspaceSwitcher - Dropdown component for switching between workspaces
 *
 * Implements the Figma design with:
 * - Trigger: Current workspace with icon and name
 * - Dropdown: List of workspaces with plan/members info, add workspace, logout
 */
export function WorkspaceSwitcher({
  currentWorkspace,
  workspaces,
  userEmail,
  isCollapsed = false,
  disabled = false,
  onWorkspaceChange,
  onAddWorkspace,
  onLogout,
}: WorkspaceSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isBookDemoOpen, setIsBookDemoOpen] = useState(false);
  const { messages } = useIntl();

  const handleWorkspaceClick = (workspace: WorkspaceItem) => {
    if (workspace.id === currentWorkspace?.id) return;
    onWorkspaceChange?.(workspace);
    setIsOpen(false);
  };

  // Collapse portal memberships into one row per owning OEM. A user invited into
  // several portals of the same OEM should see that OEM only once; selecting it
  // navigates into the OEM's first portal (the sidebar then lists them all).
  const switcherRows = React.useMemo(() => {
    const rows: {
      key: string;
      target: WorkspaceItem;
      label: string;
      icon?: string;
      isActive: boolean;
    }[] = [];
    const seenOemIds = new Set<string>();
    for (const ws of workspaces) {
      if (ws.type === "PORTAL") {
        const groupKey = ws.oem_id ?? ws.id;
        if (seenOemIds.has(groupKey)) continue;
        seenOemIds.add(groupKey);
        const groupPortals = workspaces.filter(
          (w) => w.type === "PORTAL" && (w.oem_id ?? w.id) === groupKey,
        );
        rows.push({
          key: groupKey,
          target: groupPortals[0],
          label: `${ws.name} Portal`,
          icon: ws.icon,
          isActive: groupPortals.some((p) => p.id === currentWorkspace?.id),
        });
      } else {
        rows.push({
          key: ws.id,
          target: ws,
          label: ws.name,
          icon: ws.icon,
          isActive: ws.id === currentWorkspace?.id,
        });
      }
    }
    return rows;
  }, [workspaces, currentWorkspace?.id]);

  // Render collapsed state (icon only)
  if (isCollapsed) {
    return (
      <div className="flex items-center justify-center w-full h-[32px]">
        <div className="relative w-[32px] h-[32px] shrink-0 rounded-md overflow-hidden bg-primary flex items-center justify-center">
          {currentWorkspace?.icon ? (
            <img
              src={currentWorkspace?.icon}
              alt={currentWorkspace?.name}
              className="w-full h-full object-cover rounded-md"
            />
          ) : (
            <MakulaIcon
              icon={<MakulaLogoIcon />}
              className="w-[17px] h-[20px] text-brand"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex items-center gap-sm w-full h-[32px] bg-transparent border-0 p-0 ${
          disabled ? "cursor-default" : "cursor-pointer"
        }`}
      >
        {/* Workspace Icon */}
        <div className="relative w-[32px] h-[32px] shrink-0 rounded-md overflow-hidden bg-primary flex items-center justify-center">
          {currentWorkspace?.icon ? (
            <img
              src={currentWorkspace?.icon}
              alt={currentWorkspace?.name}
              className="w-full h-full object-cover rounded-md"
            />
          ) : (
            <MakulaIcon
              icon={<MakulaLogoIcon />}
              className="w-[17px] h-[20px] text-brand"
            />
          )}
        </div>

        {/* Workspace Name */}
        <Label
          size={LABEL_SIZES.MEDIUM}
          color="text-primary"
          className="overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left"
        >
          {currentWorkspace?.name}
        </Label>

        {/* Dropdown Chevron */}
        {!disabled && (
          <MakulaIcon
            icon={<ChevronDownIcon />}
            className="w-xl h-xl shrink-0 text-secondary"
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content */}
          <div className="absolute top-full left-0 mt-md z-50 w-full min-w-[280px] bg-primary rounded-lg shadow-lg border-0">
            {/* Current Workspace Info */}
            <div className="flex flex-col gap-2xs px-lg py-sm">
              <div className="flex items-center gap-sm w-full">
                {/* Workspace Icon */}
                <div className="relative w-[32px] h-[32px] shrink-0 rounded-md overflow-hidden bg-primary flex items-center justify-center">
                  {currentWorkspace?.icon ? (
                    <img
                      src={currentWorkspace?.icon}
                      alt={currentWorkspace?.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <MakulaIcon
                      icon={<MakulaLogoIcon />}
                      className="w-[17px] h-[20px] text-brand"
                    />
                  )}
                </div>

                {/* Name and Details */}
                <div className="flex flex-col gap-2xs flex-1">
                  <Label
                    size={LABEL_SIZES.MEDIUM}
                    color="text-primary"
                    className="overflow-hidden text-ellipsis whitespace-nowrap"
                    style={{ maxWidth: "200px" }}
                  >
                    {currentWorkspace?.name}
                  </Label>
                  {(currentWorkspace?.plan || currentWorkspace?.members) && (
                    <BodyText
                      size={BODY_TEXT_SIZES.X_SMALL}
                      color="text-secondary"
                      className="flex items-center gap-2xs"
                    >
                      {currentWorkspace?.plan && (
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {currentWorkspace?.plan}
                        </span>
                      )}
                      {currentWorkspace?.plan && currentWorkspace?.members && (
                        <span>•</span>
                      )}
                      {currentWorkspace?.members && (
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {currentWorkspace?.members} Members
                        </span>
                      )}
                    </BodyText>
                  )}
                </div>
              </div>
            </div>
            <hr className="border-0 border-t border-solid border-primary m-0" />

            {/* Workspaces List */}
            <div className="flex flex-col gap-2xs p-sm">
              {/* User Email */}
              {userEmail && (
                <div className="flex items-center px-sm py-xs">
                  <Label
                    size={LABEL_SIZES.SMALL}
                    color="text-secondary"
                    className="overflow-hidden text-ellipsis whitespace-nowrap flex-1"
                  >
                    {userEmail}
                  </Label>
                </div>
              )}
              {/* Workspace Items (portals grouped by owning OEM) */}
              {switcherRows.map((row) => {
                return (
                  <button
                    key={row.key}
                    onClick={() => {
                      if (row.isActive) {
                        setIsOpen(false);
                        return;
                      }
                      handleWorkspaceClick(row.target);
                    }}
                    className={`
                      flex items-center gap-sm h-[32px] px-sm py-xs rounded-md
                      transition-colors duration-200 cursor-pointer w-full border-0 bg-inverse
                      ${
                        row.isActive
                          ? "!bg-accent"
                          : "hover:bg-accent hover:text-primary"
                      }
                    `}
                  >
                    {/* Workspace Icon */}
                    <div className="relative w-[20px] h-[20px] shrink-0 rounded-md overflow-hidden bg-primary flex items-center justify-center">
                      {row.icon ? (
                        <img
                          src={row.icon}
                          alt={row.label}
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (
                        <MakulaIcon
                          icon={<MakulaLogoIcon />}
                          className="w-[11px] h-[13px] text-brand"
                        />
                      )}
                    </div>

                    {/* Workspace Name */}
                    <BodyText
                      size={BODY_TEXT_SIZES.SMALL}
                      color="text-primary"
                      className="overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left"
                    >
                      {row.label}
                    </BodyText>

                    {/* Check Icon for Active */}
                    {row.isActive && (
                      <MakulaIcon
                        icon={<CheckIcon />}
                        className="w-xl h-xl shrink-0 text-brand"
                      />
                    )}
                  </button>
                );
              })}
              {/* Add Workspace */}
              {onAddWorkspace && (
                <button
                  onClick={() => {
                    setIsBookDemoOpen(true);
                    setIsOpen(false);
                  }}
                  className="flex items-center bg-inverse gap-sm h-[32px] px-sm py-xs rounded-md hover:bg-brand-lightest transition-colors duration-200 cursor-pointer w-full border-0"
                >
                  <MakulaIcon
                    icon={<Plus className="text-brand-lightest" />}
                    className="w-xl h-xl shrink-0 text-brand"
                  />
                  <BodyText
                    size={BODY_TEXT_SIZES.SMALL}
                    color="text-brand"
                    className="overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left"
                  >
                    {messages.login["addWorkspace"]}{" "}
                  </BodyText>
                </button>
              )}
            </div>
            <hr className="border-0 border-t border-solid border-primary m-0" />

            {/* Logout */}
            {onLogout && (
              <div className="flex flex-col gap-2xs p-sm">
                <button
                  onClick={() => {
                    onLogout();
                    setIsOpen(false);
                  }}
                  className="flex items-center gap-sm h-[32px] px-sm py-xs rounded-md hover:bg-tertiary transition-colors duration-200 cursor-pointer w-full bg-transparent border-0"
                >
                  <MakulaIcon
                    icon={<LogoutIcon />}
                    className="w-xl h-xl shrink-0 text-secondary"
                  />
                  <BodyText
                    size={BODY_TEXT_SIZES.SMALL}
                    color="text-secondary"
                    className="overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left"
                  >
                    {messages.login["logOut"]}
                  </BodyText>
                </button>
              </div>
            )}
          </div>
        </>
      )}
      <BookDemoModal
        isOpen={isBookDemoOpen}
        onClose={() => setIsBookDemoOpen(false)}
        onSubmit={onAddWorkspace}
      />
    </div>
  );
}
